import type { BlobLike } from "@pdfking/compatibility"
import type { Engine, TrackedTask } from "@pdfking/taskmaker"
import type { AsyncSequence, Bytes } from "@pdfking/tfunc"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { ContentType } from "../types/files/ContentType"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { BlobArray, BlobToUint8, FileCache } from "@pdfking/compatibility"
import {
  CopyIterable,
  GuidToString,
  IterableToArray,
  MapAsync,
  ReduceAsync,
  SafeIterable,
  UInt8Hash,
  UnixTime,
} from "@pdfking/tfunc"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

export const GenerateFile = async (
  _engine: Engine<EngineTasks>,
  { file_cache, tracking_chunk_size }: PdfEngineOptions,
  stream: AsyncSequence<BlobLike>,
  content_type: ContentType,
  emit: (task: TrackedTask) => void
): Promise<GeneratedFile> => {
  const { error, stream: copy } = SafeIterable(stream)

  const [chunks, body_size, body_hash] = CopyIterable(copy, 3)

  const guid = UInt8Hash(MapAsync(BlobToUint8, body_hash)).then(GuidToString)
  const size = await ReduceAsync(
    (acc: Bytes, blob: BlobLike) => (acc + blob.size) as Bytes,
    body_size,
    0 as Bytes
  )

  const emitter = (done: 0 | 1 = 0) => {
    if (size > tracking_chunk_size) {
      emit({ done, name: TrackedTaskName.GENERATE_FILE, total: 1 })
    }
  }

  emitter()

  const e = await error
  if (e) throw e

  const modified = UnixTime()

  await FileCache(file_cache).set(
    await guid,
    await IterableToArray(chunks).then(BlobArray)
  )

  emitter(1)

  return {
    enum: VirtualFileType.GENERATED_FILE,
    file_meta: {},
    guid: await guid,
    modified,
    size,
    type: content_type,
  }
}
